import { useRef, useState } from "react";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import AsyncSelect from "react-select/async";

function getTargetManufacturedProductId(option) {
  if (!option) return null;
  return (
    option.manufactured_product_id ??
    option.production_product_id ??
    option.production_id ??
    option.id ??
    option.value ??
    null
  );
}

export default function ReassignCartonsPanel(props) {
  const {
    singleItem,
    setOpen,
    onSearchLotNumbers,
    reassignPackagingsById,
    onRefreshTrace,
    sourceProductionRunName,
  } = props;
  const asyncRef = useRef(null);
  const [selectedLot, setSelectedLot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const cartons = Array.isArray(singleItem?.cartons) ? singleItem.cartons : [];
  const cartonIds = cartons.map((carton) => carton.id).filter(Boolean);

  const loadOptions = async function (input, callback) {
    const result = await onSearchLotNumbers(input);
    callback(result || []);
  };

  const closePanel = () => {
    setOpen({ childComponent: null, open: false });
  };

  const onConfirm = async () => {
    const manufacturedProductId = getTargetManufacturedProductId(selectedLot);
    if (!manufacturedProductId || cartonIds.length < 1 || submitting) return;

    setSubmitting(true);
    const callResult = await reassignPackagingsById(
      manufacturedProductId,
      cartonIds,
    );
    setSubmitting(false);

    if (callResult.success) {
      closePanel();
      if (onRefreshTrace) {
        await onRefreshTrace();
      }
    }
  };

  const canSubmit =
    !submitting &&
    cartonIds.length > 0 &&
    getTargetManufacturedProductId(selectedLot) != null;
  const formatBarcode = (barcode) => {
    const value = String(barcode ?? "");
    if (value.length <= 10) return value;
    return `...${value.slice(-10)}`;
  };

  return (
    <div className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-scroll py-6">
        <div className="px-4 sm:px-6">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
                Reassign cartons
              </Dialog.Title>
              <p className="mt-1 text-md text-gray-700">
                Reassigning {cartons.length} cartons of:{" "}
                <span className="font-bold">{singleItem?.product_name}</span> -
                (<span className="font-bold">{singleItem?.plu}</span>)
              </p>
              <p className="mt-1 text-md text-gray-700">
                Current lot number:{" "}
                <span className="font-bold">{singleItem?.lot_number}</span>
              </p>
              <p className="mt-1 text-md text-gray-700">
                Current production run:{" "}
                <span className="font-bold">
                  {sourceProductionRunName || "N/A"}
                </span>
              </p>
            </div>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                onClick={closePanel}
              >
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        <div className="relative mt-6 flex-1 px-4 sm:px-6">
          <div className="mb-4">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Target production run lot number
            </label>
            <AsyncSelect
              cacheOptions
              loadOptions={loadOptions}
              options={[]}
              className="w-full"
              ref={asyncRef}
              onChange={(option) => setSelectedLot(option || null)}
              value={selectedLot}
            />
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200">
            <div className="max-h-[26rem] overflow-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      Name
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      Lot Number
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      Barcode
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      Packaged Date
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      Brand
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white text-sm text-gray-700">
                  {cartons.length > 0 ? (
                    cartons.map((carton) => (
                      <tr key={carton.id}>
                        <td className="px-3 py-2">{carton.name}</td>
                        <td className="px-3 py-2">
                          {carton.lot_number ||
                            carton.lot_number ||
                            singleItem?.lot_number ||
                            "N/A"}
                        </td>
                        <td className="px-3 py-2">
                          {formatBarcode(carton.barcode)}
                        </td>
                        <td className="px-3 py-2">{carton.packaged_date}</td>
                        <td className="px-3 py-2">{carton.brand}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="px-3 py-4 text-gray-500" colSpan={6}>
                        No cartons available for reassignment.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-3 px-4 sm:px-6">
          <button
            type="button"
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            onClick={closePanel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={!canSubmit}
            onClick={onConfirm}
          >
            {submitting ? "Saving..." : "OK"}
          </button>
        </div>
      </div>
    </div>
  );
}
